import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceAirtable,
  UserImportDataSourceStatus,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { isEqual } from 'lodash';
import { Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { Label } from '../../../../../../components/CustomInputLabel';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import { DeleteButton } from '../../../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import {
  FormRow,
  FormSection
} from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer, FlexItem } from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { removeDoc, setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  COMPOSITE_MAPPINGS,
  CustomImportMultiMappingForm2,
  getDefaultMappings
} from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.AIRTABLE;

export const createAirtableViewButton = (
  d: Doc<UserImportDataSourceAirtable>
) => ({
  href: `https://airtable.com/${d.data.baseId}/${d.data.tableId}`,
  disabled: !d.data.baseId || !d.data.tableId
});

const AirtableConfiguration = ({
  source,
  onClose,
  customFields,
  onDelete
}: {
  source: Doc<UserImportDataSourceAirtable>;
  onClose: () => void;
  customFields: CustomUserField[];
  onDelete?: () => Promise<any>;
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [state, setState] = useState(source);

  const isDataChanged = () =>
    !isEqual(source.data.customMappings, state.data.customMappings) ||
    !isEqual(source.data.inferredOtherUsers, state.data.inferredOtherUsers) ||
    !isEqual(source.data.dateFormats, state.data.dateFormats);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormRow>
          <Alert severity="success" icon={<Users />}>
            {t(
              'Airtable is currently setup as a user import source.  New or updated records in the table below will be imported to Dado.'
            )}
          </Alert>
        </FormRow>
        <FormSection>
          <Label
            label={t('Source Name')}
            subLabel={t('For information only. Visible only in Dado.')}
          />
          <TextField
            fullWidth
            value={state.data.name}
            onChange={(event) =>
              setState((d) => ({
                ...d,
                data: {
                  ...d.data,
                  name: event.target.value
                }
              }))
            }
          />
        </FormSection>
        <FormSection>
          <FlexContainer spacing={3}>
            <FlexItem flex={1}>
              <Label label={t('Base ID')} />
              <TextField
                fullWidth
                value={state.data.baseId}
                onChange={(ev) =>
                  setState((x) => ({
                    ...x,
                    data: { ...x.data, baseId: ev.target.value }
                  }))
                }
              />
            </FlexItem>
            <FlexItem flex={1}>
              <Label label={t('Table ID')} />
              <TextField
                fullWidth
                value={state.data.tableId}
                onChange={(ev) =>
                  setState((x) => ({
                    ...x,
                    data: { ...x.data, tableId: ev.target.value }
                  }))
                }
              />
            </FlexItem>
          </FlexContainer>
        </FormSection>
        <FormRow>
          <Button
            style={{ width: '100%' }}
            color="primary"
            target="_blank"
            rel="noopener noreferrer"
            href={`https://airtable.com/${state.data.baseId}/${state.data.tableId}`}
            disabled={!state.data.baseId || !state.data.tableId}
          >
            {t('Open table in new tab')}
          </Button>
        </FormRow>
        <FormSection>
          <DateFormatPicker
            value={state.data.dateFormats || []}
            onChange={(formats) =>
              setState((x) => ({
                ...x,
                data: { ...x.data, dateFormats: formats }
              }))
            }
          />
        </FormSection>
        <CustomImportMultiMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          enableSave={isDataChanged()}
          integrationName="Airtable"
          userFields={userFields}
          value={{
            main: state.data.customMappings,
            others: state.data.inferredOtherUsers?.map((v) => v.customMappings)
          }}
          onChange={(nextVal) =>
            setState((x) => ({
              ...x,
              data: {
                ...x.data,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((x) => ({
                    customMappings: x
                  })) || []
              }
            }))
          }
        />
      </DialogContent>
      <DialogActionsWithSlots
        left={
          onDelete && (
            <DeleteButton
              variant="contained"
              color="secondary"
              onDelete={onDelete}
              keyword={source.id}
              dialogTitle={t('Remove integration') + '?'}
              buttonAction={t('Remove integration')}
            >
              {t('Remove integration')}
            </DeleteButton>
          )
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>

            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={!state.data.baseId || !state.data.tableId}
              onClick={() => setDoc(state).then(onClose)}
              pending={`${t('Saving')}...`}
            >
              {t('Save settings')}
            </ButtonWithPromise>
          </>
        }
      />
    </>
  );
};

export const AirtableSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();

  const source: Doc<UserImportDataSourceAirtable> = useMemo(() => {
    return {
      id: shortid(),
      collection: FS.userImportDataSources,
      data: {
        orgId: orgId,
        type: UserImportSourceType.AIRTABLE,
        status: UserImportDataSourceStatus.PENDING,
        detailedStatus: '',
        lastUpdate: null,
        createdBy: userId,
        createdAt: now(),
        customMappings: [
          COMPOSITE_MAPPINGS['$FULL_NAME$'],
          ...getDefaultMappings(customFields, t)
        ],
        baseId: '',
        tableId: '',
        name: ''
      }
    };
  }, [userId, orgId, customFields, t]);

  return (
    <AirtableConfiguration
      source={source}
      onClose={onClose}
      customFields={customFields}
    />
  );
};

export const AirtableExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceAirtable>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  return (
    <AirtableConfiguration
      source={d}
      onClose={onClose}
      onDelete={() => removeDoc(d).then(onClose)}
      customFields={customFields}
    />
  );
};

export const ImportSourceRowAirtable = ({
  d
}: {
  d: Doc<UserImportDataSourceAirtable>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={d.data.name || META.name}
      logoUrl={META.smallLogoUrl}
      viewButton={{
        href: `https://airtable.com/${d.data.baseId}/${d.data.tableId}`,
        disabled: !d.data.baseId || !d.data.tableId
      }}
    />
  );
};
